import { SourcingEventStatus, type SourcingEventV2Dto } from "../../../../autogen/bff-api";
import { autoKarensDays } from "../../../sourcing-events/buyer/public/utils/karens";
import type { MilestoneKey } from "../../../sourcing-events/buyer/public/utils/milestones";

export interface TimelinePhase {
  visible: boolean;
  inert: boolean;
  activeKey: MilestoneKey | null;
  completedKeys: MilestoneKey[];
}

// Mirrors MILESTONE_NODES order. The Karens (standstillExpiry) phase is dropped for procurements
// with no statutory standstill — Part II/I/outside-FOA, per Standstill.kt / FOA § 25-2.
const FULL_PHASE_ORDER: readonly MilestoneKey[] = [
  "announcement",
  "bidDeadline",
  "evaluationStart",
  "awardDecision",
  "standstillExpiry",
  "contractSigning",
];

export const phasesFor = (event: SourcingEventV2Dto): MilestoneKey[] => {
  const hasStandstill = autoKarensDays(event.publicContext.foaPart ?? null) != null;
  return FULL_PHASE_ORDER.filter((key) => key !== "standstillExpiry" || hasStandstill);
};

const HIDDEN_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Draft,
  SourcingEventStatus.PublicationPending,
  SourcingEventStatus.PublicationFailed,
]);

const HIDDEN: TimelinePhase = { visible: false, inert: false, activeKey: null, completedKeys: [] };

const hasPassed = (iso: string | null | undefined, now: number): boolean =>
  iso != null && now >= Date.parse(iso);

// `now` is injected (epoch ms) rather than read from a clock, so the date-driven transitions
// (① → ② at bid deadline, ⑤ → ⑥ at standstill expiry) are deterministic in tests.
export const deriveTimelinePhase = (event: SourcingEventV2Dto, now: number): TimelinePhase => {
  const { status } = event;
  if (HIDDEN_STATUSES.has(status)) return HIDDEN;

  const phases = phasesFor(event);

  if (status === SourcingEventStatus.Cancelled) {
    return { visible: true, inert: true, activeKey: null, completedKeys: [] };
  }
  if (status === SourcingEventStatus.Closed) {
    return { visible: true, inert: false, activeKey: null, completedKeys: [...phases] };
  }

  const activeKey = activeKeyFor(event, now, phases);
  if (activeKey == null) return HIDDEN;

  const activeIndex = phases.indexOf(activeKey);
  return { visible: true, inert: false, activeKey, completedKeys: phases.slice(0, activeIndex) };
};

const activeKeyFor = (
  event: SourcingEventV2Dto,
  now: number,
  phases: readonly MilestoneKey[],
): MilestoneKey | null => {
  switch (event.status) {
    case SourcingEventStatus.Published:
      return hasPassed(event.eventData.bidSubmissionDeadline, now) ? "bidDeadline" : "announcement";
    case SourcingEventStatus.Evaluation:
      return "evaluationStart";
    case SourcingEventStatus.AwardDecision:
      return "awardDecision";
    case SourcingEventStatus.Awarded:
      if (!phases.includes("standstillExpiry")) return "contractSigning";
      return hasPassed(event.award?.standstillExpiresAt, now) ? "contractSigning" : "standstillExpiry";
    default:
      return null;
  }
};
